import React, { type ReactElement } from 'react';
import { Loader, NavBar, NavItem } from '@deque/cauldron-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Header from '../common/components/Header';
import MainLayout from '../common/components/MainLayout';
import { PRODUCT_URLS, ProductSlugs } from '../common/constants';
import AxeDevToolsRoutes from './components/AxeDevToolsRoutes';
import { useRouteMatch } from 'react-router';
import { useFeatureFlagState } from '../common/contexts/featureFlags';
import { useProducts } from '../common/contexts/products';
import ProductHeader from '../common/components/ProductHeader';
import ProductLogo from '../common/components/ProductLogo';
import { useServerInfo } from '../common/contexts/serverInfo';

const AxeDevTools = (): ReactElement => {
  const { t } = useTranslation();
  const { loading, getProductBySlug } = useProducts();
  const hasBillingV1 = useFeatureFlagState('billing_v1');
  const { serverInfo } = useServerInfo();
  const loaderRef = React.useRef<HTMLDivElement>(null);
  const showPlans = hasBillingV1 && serverInfo?.isSelfProvisioningEnabled;
  const homeIsActive = !!useRouteMatch({
    path: PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root,
    exact: true
  });
  const plansIsActive = !!useRouteMatch({
    path: PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].plans
  });
  const isTestsRoute = useRouteMatch({
    path: `${PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root}/tests`
  });
  const isUfaRoute = useRouteMatch({
    path: `${PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root}/user-flows`
  });
  const isCustomIntegrationHandler = !!useRouteMatch({
    path: `${PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root}/test-setup`
  });

  const isExtensionNotInstalledRoute = !!useRouteMatch({
    path: PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].notInstalled
  });

  const navBarIsVisible =
    !isTestsRoute &&
    !isUfaRoute &&
    !isCustomIntegrationHandler &&
    !isExtensionNotInstalledRoute;
  const shouldShowFooter =
    !isCustomIntegrationHandler && !isExtensionNotInstalledRoute;

  React.useEffect(() => {
    loaderRef.current?.focus();
  }, [loaderRef.current]);

  if (loading) {
    return <Loader label={t('Loading...')} tabIndex={-1} ref={loaderRef} />;
  }

  const axeDevToolsProduct = getProductBySlug(
    ProductSlugs.axeDevToolsExtension
  );

  const nav = navBarIsVisible ? (
    <NavBar>
      <NavItem active={homeIsActive}>
        <Link
          to={PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root}
          aria-current={homeIsActive}
        >
          {t('WELCOME')}
        </Link>
      </NavItem>
      {showPlans && (
        <NavItem active={plansIsActive}>
          <Link
            to={PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].plans}
            aria-current={plansIsActive}
          >
            {t('PLANS')}
          </Link>
        </NavItem>
      )}
    </NavBar>
  ) : undefined;

  // axeDevToolsProduct will be undefined if the user is on-prem
  const home = axeDevToolsProduct ? (
    <ProductHeader
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      product={axeDevToolsProduct!}
      homeURL={PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root}
    />
  ) : (
    <ProductLogo
      title={t('axe DevTools Extension')}
      homeURL={PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root}
    />
  );

  const header = !isCustomIntegrationHandler && (
    <Header navBar={nav} home={home} />
  );

  return (
    <MainLayout
      header={header}
      hasBottomNav={navBarIsVisible}
      shouldShowFooter={shouldShowFooter}
    >
      <AxeDevToolsRoutes />
    </MainLayout>
  );
};

export default AxeDevTools;
